import { useState } from "react";

import { MCP_CLIENTS_PATH, redirectHost, type McpClientWriteResponse } from "../../api/mcp";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useWrite } from "../../hooks/useWrite";
import { Gate } from "../authority/Gate";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { effectFromSeconds, WriteResult } from "../write/EffectTiming";
import { WriteFailure } from "../write/WriteFailure";

/**
 * Allow or remove one AI app (an OAuth redirect URI) on MCP's allowlist.
 *
 * Both directions are L2: a modal, a required reason (it goes onto MCP's admin audit),
 * and the effect timing from the API's own number. Allowing is the dangerous one — an
 * entry is the address a merchant's sign-in result is sent to — so it says so, and an
 * unfamiliar domain gets the phishing warning before the operator types a reason.
 *
 * Removing blocks NEW sign-ins only: merchants already connected through the app keep
 * working (MCP decided this; removal does not end sessions). The dialog says that too,
 * so nobody expects "remove" to disconnect anyone.
 */
export function AppAccessControl({
  config,
  uri,
  mode,
  knownHost,
  label,
  onDone,
}: {
  config: AppConfig;
  uri: string;
  mode: "allow" | "remove";
  /** From MCP's requested list; undefined for a typed-in address (warn by default). */
  knownHost?: boolean;
  label?: string;
  onDone: () => void;
}) {
  const write = useWrite<McpClientWriteResponse>(config);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<McpClientWriteResponse | null>(null);
  const allowing = mode === "allow";
  const host = redirectHost(uri);

  function submit(reason: string) {
    void write
      .submit(MCP_CLIENTS_PATH, {
        method: allowing ? "POST" : "DELETE",
        body: JSON.stringify({ uri, reason }),
      })
      .then((r) => {
        if (!r) return;
        setResult(r);
        setOpen(false);
        onDone(); // re-read: a lost reply is indistinguishable from a failed write
      });
  }

  return (
    <>
      <Gate need="settings:write">
        <Button color={allowing ? undefined : "red"} outline={!allowing} onClick={() => setOpen(true)}>
          {label ?? (allowing ? "Allow…" : "Remove…")}
        </Button>
      </Gate>

      {result && (
        <div className="mt-3">
          <WriteResult title={allowing ? "Allowed" : "Removed"} onDismiss={() => setResult(null)}>
            <p>
              {allowing
                ? `Sign-ins from ${host} are accepted. `
                : `New sign-ins from ${host} are refused; merchants already connected keep working. `}
              {effectFromSeconds(result.live_within_seconds, "This")}
            </p>
          </WriteResult>
        </div>
      )}

      <ConfirmDialog
        open={open}
        title={allowing ? `Allow ${host}` : `Remove ${host}`}
        confirmLabel={allowing ? "Allow this app" : "Remove this app"}
        destructive={!allowing}
        requireReason
        reasonHint={allowing ? "e.g. merchant 808803 asked to connect Gemini" : "e.g. no longer used"}
        busy={write.state.kind === "submitting"}
        error={<WriteFailure state={write.state} />}
        onCancel={() => setOpen(false)}
        onConfirm={submit}
        effect="Every MCP server picks this up within about 60 seconds. No restart."
      >
        <p className="break-all">
          <span className="ref">{uri}</span>
        </p>
        {allowing ? (
          <>
            <p>
              MCP will send merchants' sign-in results to this address, so whoever controls{" "}
              <strong>{host}</strong> can read the data of any merchant who connects through it.
            </p>
            {knownHost !== true && (
              <p className="text-amber-700 dark:text-amber-400">
                {host} is not a domain we recognise. Make sure it belongs to the app the merchant
                named before allowing it: a lookalike address is how a phishing app would get in.
              </p>
            )}
          </>
        ) : (
          <p>
            New sign-ins from this app are refused. Merchants already connected through it keep
            working; this does not end their sessions.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}
